import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import Pagination from '@/components/feature/Pagination';
import FilterPanel, { type FilterState } from '@/pages/category/components/FilterPanel';
import { categories, sortOptions } from '@/mocks/categories';
import { movies } from '@/mocks/movies';
import { usePageMeta } from '@/hooks/usePageMeta';

const PAGE_SIZE = 10;
const EMPTY_FILTERS: FilterState = { genre: '', region: '', year: '' };

export default function CategoryPage() {
  const { categoryId = '' } = useParams();
  const category = categories.find((cat) => cat.id === categoryId);

  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [sort, setSort] = useState('latest');
  const [page, setPage] = useState(1);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  usePageMeta({
    title: category ? `${category.name} - 河马AV影视内容平台` : '分类 - 河马AV影视内容平台',
    description: category
      ? `浏览最新${category.name}影视资源与介绍：${category.description}。`
      : '浏览河马AV各类影视资源与介绍。',
    keywords: category ? `${category.name},河马AV,影视资源,${category.name}推荐` : '河马AV,影视分类',
  });

  useEffect(() => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
  }, [categoryId]);

  const filtered = useMemo(() => {
    if (!category) return [];
    const list = movies.filter((movie) => {
      if (movie.categoryId !== category.id) return false;
      if (filters.genre && !movie.genres.includes(filters.genre)) return false;
      if (filters.region && movie.region !== filters.region) return false;
      if (filters.year && String(movie.year) !== filters.year) return false;
      return true;
    });

    return list.sort((a, b) => {
      if (sort === 'hot') return b.score - a.score || b.year - a.year;
      if (sort === 'score') return b.score - a.score || a.title.localeCompare(b.title);
      return b.year - a.year || b.score - a.score;
    });
  }, [category, filters, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const hasFilters = Boolean(filters.genre || filters.region || filters.year);

  function resetFilters() {
    setFilters(EMPTY_FILTERS);
    setPage(1);
  }

  function handleFilterChange(next: FilterState) {
    setFilters(next);
    setPage(1);
  }

  if (!category) {
    return (
      <Layout>
        <div className="mx-auto flex max-w-3xl flex-col items-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-3xl text-secondary-600">
            <i className="ri-emotion-sad-line" />
          </span>
          <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">未找到该分类</h1>
          <p className="mt-3 text-sm text-foreground-500">您访问的影视分类不存在，请从以下分类中重新选择。</p>
          <Link
            to="/"
            className="mt-6 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-home-4-line" />
            返回首页
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header */}
      <section className="border-b border-background-200 bg-background-50">
        <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: '影视分类' }, { label: category.name }]} />
          <div className="mt-4 flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-2xl text-primary-600">
              <i className={category.icon} />
            </span>
            <div>
              <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                {category.name}
              </h1>
              <p className="mt-1 text-sm text-foreground-500">{category.description}</p>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[260px_1fr]">
          {/* Sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <FilterPanel
                filters={filters}
                onChange={handleFilterChange}
                onReset={resetFilters}
                showReset={hasFilters}
              />
            </div>
          </aside>

          {/* Content */}
          <div>
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-foreground-500">
                共 <span className="font-label font-bold text-primary-600">{filtered.length}</span> 部作品
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setMobileFilterOpen((v) => !v)}
                  className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-sm text-foreground-700 lg:hidden"
                >
                  <i className="ri-filter-3-line" />
                  筛选
                </button>
                <div className="flex items-center gap-1 rounded-full bg-background-200 p-1">
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => { setSort(option.value); setPage(1); }}
                      className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                        sort === option.value
                          ? 'bg-background-50 font-medium text-primary-600 shadow-sm'
                          : 'text-foreground-600 hover:text-primary-600'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Mobile filter panel */}
            {mobileFilterOpen && (
              <div className="mb-5 animate-fade-in lg:hidden">
                <FilterPanel
                  filters={filters}
                  onChange={handleFilterChange}
                  onReset={resetFilters}
                  showReset={hasFilters}
                />
              </div>
            )}

            {pageItems.length > 0 ? (
              <>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4 xl:grid-cols-4">
                  {pageItems.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
                <Pagination page={page} totalPages={totalPages} onChange={setPage} />
              </>
            ) : (
              <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-20 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-3xl text-secondary-500">
                  <i className="ri-film-line" />
                </span>
                <h2 className="mt-5 font-heading text-lg font-bold text-foreground-900">
                  暂无相关影视作品
                </h2>
                <p className="mt-2 text-sm text-foreground-500">您可以重置筛选条件，浏览更多内容。</p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-6 flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-accent-600"
                >
                  <i className="ri-refresh-line" />
                  重置筛选条件
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}